"use client";
import React, { useState } from "react";
import { DrupalNode } from "next-drupal";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { MdChevronRight } from "react-icons/md";
import { config } from "@/lib/config";

interface PartnerCardPaginationProps {
  sliderData: DrupalNode[];
  isHomePage?: boolean;
}

const PartnerCardPagination: React.FC<PartnerCardPaginationProps> = ({
  sliderData,
  isHomePage = false,
}) => {
  const ITEMS_PER_PAGE = 16; // 4 cards per row × 4 rows = 16 cards per page
  const total = sliderData?.length || 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const [currentPage, setCurrentPage] = useState(1);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = sliderData.slice(startIndex, endIndex);

  const renderCard = (partnerCard: DrupalNode, index: number) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="px-[15px] py-5 mobileMax:px-2 mobileMax:py-3"
    >
      <Link
        href={partnerCard?.field_mission_card_link}
        target="_blank"
        className="group relative w-full p-3 mobileMax:px-2 bg-white rounded-[20px] overflow-hidden 
					shadow-[0_2px_5px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)]
					mobileMax:p-1 min-w-0 min-h-[200px] betweenMobileTab:min-h-[150px] mobileMax:min-h-[120px] mobileMax:h-[120px] flex items-center justify-center duration-300 transition-all"
      >
        <Image
          src={`${config.apiBase}${partnerCard?.field_mission_card_image?.uri?.url}`}
          alt={partnerCard?.field_mission_card_image?.filename}
          width={200}
          height={200}
          className={`mobileMax:max-w-[130px] max-h-[110px] mobileMax:max-h-[80px] ${
            isHomePage ? "object-contain" : ""
          }`}
        />
        <div className="absolute bottom-0 left-0 w-full translate-y-full group-hover:translate-y-0 transition-transform duration-500">
          <div className="w-full h-[30px] bg-[linear-gradient(to_right,#8EBCE5,#11375C)] flex flex-col items-center text-white font-medium text-sm">
            <div className="w-full rounded-b-[15px] h-[15px] bg-white" />
            <p className="inline-flex mt-[2px]">
              Visit Web
              <MdChevronRight className="w-[18px] h-[18px] max-w-[18px] ml-0.5" />
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleLast = () => {
    setCurrentPage(totalPages);
  };

  return (
    <div className="w-full">
      {/* Grid Layout - 4 columns with container for spacing */}
      <div className="max-w-[1400px] mx-auto px-8 mobileMax:px-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0.5, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0.5, x: -50 }}
            transition={{ duration: 0 }}
            className="grid desktop:grid-cols-4 aboveLaptop:grid-cols-3 lieTablets:grid-cols-2 mobileMax:grid-cols-2 gap-4 mobileMax:gap-2 mb-8"
          >
            {currentPageData.map(renderCard)}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-8">
          {/* Page Numbers */}
          <div className="flex items-center bg-white rounded-lg py-2 px-4 shadow-md hover:shadow-lg">
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-8 h-8 flex items-center justify-center text-sm font-medium transition-all duration-200 rounded-md mx-1 ${
                    currentPage === pageNumber
                      ? "modals-gradientBtn text-white shadow-md transform scale-105"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            {/* Navigation Buttons */}
            <div className="flex items-center border-l border-gray-200 ml-4 pl-4">
              <button
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                className={`text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  currentPage >= totalPages
                    ? "text-gray-400"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Next {">"}
              </button>
              <button
                onClick={handleLast}
                disabled={currentPage >= totalPages}
                className={`text-sm transition-colors ml-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                  currentPage >= totalPages
                    ? "text-gray-400"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Last {">>"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCardPagination;
